import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  AddCommentInput,
  AddLineInput,
  CancelCaseInput,
  CaseDetail,
  CaseHeaderPatch,
  CaseListItem,
  CaseMutationResult,
  CommentView,
  CreateCaseInput,
  DuplicateCandidate,
  EmailEventView,
  MarkReadInput,
  MutationMeta,
  PreviewReceiptInput,
  PreviewReceiptResult,
  ReverseVerificationInput,
  SearchHit,
  SetLineExceptionInput,
  UpdateLineInput,
  VerifyReceiptInput,
} from "../../shared/contracts.js";
import { sessionQueryKey } from "../app/AppProviders.js";
import { caseQueryKeys, type InfinitePages } from "./caseQueries.js";
import { isClientApiError, request } from "./http.js";

/**
 * Parts case mutations — the arrival side: receipt preview, verified
 * receipt, verification reversal, and arrival-email retry (spec §7, §9).
 *
 * Every hook sends the case version the client last saw and never a status,
 * `arrivedAt`, a total, an actor or a recipient: the server derives all of
 * those inside its own transaction and answers with `CaseMutationResult`.
 * After any success the whole case cache is invalidated (lists, counts,
 * detail, activity, search — see {@link invalidateCaseData}) so every open
 * panel shows the server's new truth rather than a client-side guess.
 */

// ---------------------------------------------------------------------------
// DTOs as they cross the wire
// ---------------------------------------------------------------------------

/** `CaseMutationResult` as JSON: `arrivedAt` travels as an ISO string. */
export type CaseMutationResultDto = Readonly<
  Omit<CaseMutationResult, "arrivedAt"> & { arrivedAt: string | null }
>;

/**
 * The receipt DTOs (`POST /api/cases/:caseId/receipts[/preview|/reverse]`)
 * are shared contracts in `src/shared/contracts.ts` — the server routes
 * validate against the same definitions, so a shape drift is a compile error.
 * Re-exported here for the arrival dialogs.
 */
export type {
  PreviewReceiptInput,
  PreviewReceiptResult,
  ReceiptLineInput,
  ReverseVerificationInput,
  VerifyReceiptInput,
} from "../../shared/contracts.js";

/**
 * The email-event DTO (`GET /api/email-events/:emailEventId`) is a shared
 * contract in `src/shared/contracts.ts` — the server route and this client
 * read one definition, so a shape drift is a compile error rather than a
 * silently blank banner. Re-exported here for the arrival components.
 */
export type {
  EmailDeliveryView,
  EmailEventView,
} from "../../shared/contracts.js";
export type { EmailEventKind, EmailState } from "../../shared/domain.js";

// ---------------------------------------------------------------------------
// Paths and keys
// ---------------------------------------------------------------------------

export const casePaths = {
  previewReceipt: (caseId: string) =>
    `/api/cases/${encodeURIComponent(caseId)}/receipts/preview`,
  verifyReceipt: (caseId: string) =>
    `/api/cases/${encodeURIComponent(caseId)}/receipts`,
  reverseVerification: (caseId: string) =>
    `/api/cases/${encodeURIComponent(caseId)}/receipts/reverse`,
  emailEvent: (emailEventId: string) =>
    `/api/email-events/${encodeURIComponent(emailEventId)}`,
  retryEmail: (emailEventId: string) =>
    `/api/email-events/${encodeURIComponent(emailEventId)}/retry`,
} as const;

export const emailEventQueryKey = (emailEventId: string) =>
  ["email-events", emailEventId] as const;

/**
 * Marks every cached case query stale — list pages, counts, detail, activity,
 * search hits, email events — whichever module owns the key. Only the session
 * query is left alone: who is signed in did not change. Active queries refetch
 * at once; inactive ones refetch when next observed.
 */
export function invalidateCaseData(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] !== sessionQueryKey[0],
  });
}

// ---------------------------------------------------------------------------
// Error copy shared by the arrival dialogs
// ---------------------------------------------------------------------------

/** The one recovery message for an optimistic-version conflict (409 `STALE_WRITE`). */
export const STALE_WRITE_MESSAGE =
  "This order changed after you opened it. Reload the latest version before saving.";

/**
 * Plain-language copy for a failed case mutation. The arrival dialogs use
 * the default 403 wording; the order flows pass their own (`forbidden`).
 */
export function mutationErrorMessage(
  error: unknown,
  options: { forbidden?: string } = {},
): string {
  if (isClientApiError(error)) {
    if (error.status === 409) {
      if (error.code === "STALE_WRITE") return STALE_WRITE_MESSAGE;
      // Every other conflict — a cancelled order, a duplicate, an inactive
      // customer, no email on file — explains itself in the server's words.
      return error.message;
    }
    if (error.status === 0) {
      return "We can't reach the server right now. Nothing was saved. Check your connection and try again.";
    }
    if (error.status === 403) {
      return (
        options.forbidden ??
        "Your account can't change this order. Only Parts can verify or correct arrivals."
      );
    }
    if (error.status === 422) {
      const first = Object.values(error.fieldErrors)
        .flat()
        .find((message) => message.length > 0);
      return first ?? error.message;
    }
    return error.message;
  }
  return "Something went wrong. Nothing was saved. Try again.";
}

// ---------------------------------------------------------------------------
// Request functions
// ---------------------------------------------------------------------------

export function previewReceipt(
  caseId: string,
  input: PreviewReceiptInput,
): Promise<PreviewReceiptResult> {
  return request<PreviewReceiptResult>(casePaths.previewReceipt(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function verifyReceipt(
  caseId: string,
  input: VerifyReceiptInput,
): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(casePaths.verifyReceipt(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function reverseVerification(
  caseId: string,
  input: ReverseVerificationInput,
): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(casePaths.reverseVerification(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function fetchEmailEvent(emailEventId: string): Promise<EmailEventView> {
  return request<EmailEventView>(casePaths.emailEvent(emailEventId));
}

export async function retryEmail(emailEventId: string): Promise<void> {
  await request<unknown>(casePaths.retryEmail(emailEventId), {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Endpoints: the same dialogs on either side of the counter
// ---------------------------------------------------------------------------

/**
 * The arrival dialogs (`VerifyArrivalDialog`, `ReverseVerificationDialog`)
 * work a case by default; handed a wholesale order's endpoints
 * (`wholesaleReceiptEndpoints` in `wholesaleQueries.ts`) they post the very
 * same bodies to `/api/wholesale/orders/:orderId/receipts…` and answer
 * with that side's result (wholesale spec §3.3, §4.5: one engine, two
 * owners). `R` is the mutation result the side answers with.
 */
export interface ReceiptEndpoints<R = CaseMutationResultDto> {
  preview(input: PreviewReceiptInput): Promise<PreviewReceiptResult>;
  verify(input: VerifyReceiptInput): Promise<R>;
  reverse(input: ReverseVerificationInput): Promise<R>;
}

export function caseReceiptEndpoints(caseId: string): ReceiptEndpoints {
  return {
    preview: (input) => previewReceipt(caseId, input),
    verify: (input) => verifyReceipt(caseId, input),
    reverse: (input) => reverseVerification(caseId, input),
  };
}

/** `CancelCaseDialog`'s one request; a wholesale order's is `cancelWholesaleOrder`. */
export type CancelEndpoint<R = CaseMutationResultDto> = (input: CancelCaseInput) => Promise<R>;

/** `LineExceptionDialog`'s one request; a wholesale order's is `setWholesaleLineException`. */
export type LineExceptionEndpoint<R = CaseMutationResultDto> = (
  lineId: string,
  input: SetLineExceptionInput,
) => Promise<R>;

/**
 * `CommentComposer`'s request and what follows it in the cache: the case
 * side writes the comment into the case detail ({@link applyCommentToCache});
 * the wholesale side into the order detail.
 */
export interface CommentEndpoints {
  add(input: AddCommentInput): Promise<CommentView>;
  afterAdd(queryClient: QueryClient, comment: CommentView): Promise<void>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Preview is read-only: it never touches the cache. */
export function usePreviewReceipt<R = CaseMutationResultDto>(
  endpoints: ReceiptEndpoints<R>,
): UseMutationResult<PreviewReceiptResult, unknown, PreviewReceiptInput> {
  return useMutation({ mutationFn: (input: PreviewReceiptInput) => endpoints.preview(input) });
}

export function useVerifyReceipt<R = CaseMutationResultDto>(
  endpoints: ReceiptEndpoints<R>,
): UseMutationResult<R, unknown, VerifyReceiptInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: VerifyReceiptInput) => endpoints.verify(input),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

export function useReverseVerification<R = CaseMutationResultDto>(
  endpoints: ReceiptEndpoints<R>,
): UseMutationResult<R, unknown, ReverseVerificationInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReverseVerificationInput) => endpoints.reverse(input),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

/**
 * Manual retry of a logical email event. The server clears the failed
 * deliveries' scheduling and error data under the *same* event — no new
 * transition, no new recipient, the same Message-ID per delivery.
 */
export function useRetryEmail(
  emailEventId: string,
): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retryEmail(emailEventId),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

/** Poll cadence while a delivery is still in flight (the worker runs every 15 s). */
const IN_FLIGHT_REFETCH_MS = 15_000;

export function useEmailEvent(
  emailEventId: string | null,
): UseQueryResult<EmailEventView> {
  return useQuery({
    queryKey: emailEventQueryKey(emailEventId ?? ""),
    queryFn: () => fetchEmailEvent(emailEventId ?? ""),
    enabled: emailEventId !== null,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === "pending" || state === "sending"
        ? IN_FLIGHT_REFETCH_MS
        : false;
    },
  });
}

// ===========================================================================
// Comments and read state (Task 13)
//
// Every signed-in role — Parts included — may append a comment (spec §13);
// none may edit or delete one, so no such hook exists. A comment is not order
// data: it bumps neither the case version nor `updatedAt`, and the server
// advances the author's own read marker through the new event inside the
// comment's transaction.
//
// The read acknowledgement posts back the exact `readThroughActivityEventId`
// the detail response carried. The server verifies it belongs to the case and
// never moves a marker backwards, so an event created after that response
// stays unread for this person until they open the case again.
// ===========================================================================

export type { AddCommentInput, CommentView, MarkReadInput } from "../../shared/contracts.js";

export const commentPaths = {
  add: (caseId: string) => `/api/cases/${encodeURIComponent(caseId)}/comments`,
  markRead: (caseId: string) => `/api/cases/${encodeURIComponent(caseId)}/read`,
} as const;

export type MarkReadVariables = Readonly<{
  caseId: string;
  throughActivityEventId: string;
}>;

export function addComment(
  caseId: string,
  input: AddCommentInput,
): Promise<CommentView> {
  return request<CommentView>(commentPaths.add(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function markCaseRead(
  caseId: string,
  throughActivityEventId: string,
): Promise<void> {
  const input: MarkReadInput = { throughActivityEventId };
  await request<unknown>(commentPaths.markRead(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Flips `unread` off for `caseId` in every loaded list and search page. */
function clearUnreadInPages<T>(
  pages: unknown,
  caseIdOf: (item: T) => string,
  clear: (item: T) => T,
  caseId: string,
): unknown {
  if (
    typeof pages !== "object" ||
    pages === null ||
    !Array.isArray((pages as InfinitePages<T>).pages)
  ) {
    return pages;
  }
  const data = pages as InfinitePages<T>;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        caseIdOf(item) === caseId ? clear(item) : item,
      ),
    })),
  };
}

/**
 * Reflects an accepted read acknowledgement in the cache without a round
 * trip: the case's row stops showing as unread in every loaded list and
 * search page, and so does the detail. The next refetch confirms it.
 */
export function applyReadAcknowledgement(
  queryClient: QueryClient,
  caseId: string,
): void {
  queryClient.setQueryData<CaseDetail>(
    caseQueryKeys.detail(caseId),
    (current) =>
      current ? { ...current, case: { ...current.case, unread: false } } : current,
  );
  queryClient.setQueriesData<unknown>({ queryKey: caseQueryKeys.lists() }, (pages: unknown) =>
    clearUnreadInPages<CaseListItem>(
      pages,
      (item) => item.id,
      (item) => ({ ...item, unread: false }),
      caseId,
    ),
  );
  queryClient.setQueriesData<unknown>(
    { queryKey: caseQueryKeys.searches() },
    (pages: unknown) =>
      clearUnreadInPages<SearchHit>(
        pages,
        (hit) => hit.case.id,
        (hit) => ({ ...hit, case: { ...hit.case, unread: false } }),
        caseId,
      ),
  );
}

/**
 * Appends the server's comment to the cached detail, moves the author's read
 * cursor through the comment's own event (the server already did, inside the
 * comment transaction) and clears the case's unread flag for the author.
 */
export function applyCommentToCache(
  queryClient: QueryClient,
  caseId: string,
  comment: CommentView,
): void {
  queryClient.setQueryData<CaseDetail>(caseQueryKeys.detail(caseId), (current) => {
    if (!current) return current;
    const comments = current.comments.some((existing) => existing.id === comment.id)
      ? current.comments
      : [...current.comments, comment];
    return {
      ...current,
      comments,
      readThroughActivityEventId: comment.activityEventId,
      case: { ...current.case, unread: false },
    };
  });
}

/**
 * Acknowledges that the person has seen a case through a given activity
 * event. Silent on failure: an unread mark that lingers is a nuisance, not a
 * lost record, and the next open tries again.
 */
export function useMarkCaseRead(): UseMutationResult<void, unknown, MarkReadVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, throughActivityEventId }: MarkReadVariables) =>
      markCaseRead(caseId, throughActivityEventId),
    onSuccess: (_result, variables) =>
      applyReadAcknowledgement(queryClient, variables.caseId),
  });
}

/**
 * Appends a comment. On success the response is written into the cached
 * detail at once (the person sees their words, author and time immediately),
 * the detail is marked stale so its activity timeline picks up the comment's
 * event on the next refetch, and every list, search and count query is
 * invalidated so unread state elsewhere refreshes.
 */
export function useAddComment(
  endpoints: CommentEndpoints,
): UseMutationResult<CommentView, unknown, AddCommentInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddCommentInput) => endpoints.add(input),
    onSuccess: (comment) => endpoints.afterAdd(queryClient, comment),
  });
}

/** The case side's comment endpoints: post to the case route, then write the comment into the case detail. */
export function caseCommentEndpoints(caseId: string): CommentEndpoints {
  return {
    add: (input) => addComment(caseId, input),
    afterAdd: async (queryClient, comment) => {
      applyCommentToCache(queryClient, caseId, comment);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: caseQueryKeys.detail(caseId), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: caseQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: caseQueryKeys.searches() }),
        queryClient.invalidateQueries({ queryKey: caseQueryKeys.counts() }),
      ]);
    },
  };
}

// ===========================================================================
// Order commands (Task 11)
//
// Creation, header edits, part-line add/update, exceptions and cancellation
// against the Task 5 command routes. Every request carries the case version
// the client last saw (`meta.expectedVersion` / `expectedVersion`) and never
// a status, a total, an actor or a recipient; the server answers with
// `CaseMutationResult`. A 409 `DUPLICATE_CASE` names the matching cases in
// `details.candidates` so the interface can link to them; Parts proceeds by
// resubmitting the *same* input with `duplicateOverrideReason`, which the
// server audits with the matched ids.
// ===========================================================================

export type {
  AddLineInput,
  CancelCaseInput,
  CaseHeaderPatch,
  CreateCaseInput,
  DuplicateCandidate,
  MutationMeta,
  SetLineExceptionInput,
  UpdateLineInput,
} from "../../shared/contracts.js";

/** `PATCH /api/cases/:caseId` body: the header patch, the version, and the audited override after a duplicate warning. */
export type UpdateCaseHeaderInput = Readonly<{
  patch: CaseHeaderPatch;
  meta: MutationMeta;
  duplicateOverrideReason?: string;
}>;

export const orderCommandPaths = {
  create: "/api/cases",
  updateHeader: (caseId: string) => `/api/cases/${encodeURIComponent(caseId)}`,
  cancel: (caseId: string) => `/api/cases/${encodeURIComponent(caseId)}/cancel`,
  addLine: (caseId: string) => `/api/cases/${encodeURIComponent(caseId)}/lines`,
  updateLine: (caseId: string, lineId: string) =>
    `/api/cases/${encodeURIComponent(caseId)}/lines/${encodeURIComponent(lineId)}`,
  setLineException: (caseId: string, lineId: string) =>
    `/api/cases/${encodeURIComponent(caseId)}/lines/${encodeURIComponent(lineId)}/exception`,
} as const;

/** The 403 wording for the order flows (creation and editing). */
export const ORDER_FORBIDDEN_MESSAGE =
  "Your account can't change this order. Only Parts can create or edit orders.";

/** Plain-language copy for a failed order command. */
export function orderMutationErrorMessage(error: unknown): string {
  return mutationErrorMessage(error, { forbidden: ORDER_FORBIDDEN_MESSAGE });
}

/** True for the version conflict the edit flows must never overwrite. */
export function isStaleWriteError(error: unknown): boolean {
  return isClientApiError(error) && error.status === 409 && error.code === "STALE_WRITE";
}

function isDuplicateCandidate(value: unknown): value is DuplicateCandidate {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate["caseId"] === "string" &&
    typeof candidate["caseNumber"] === "string" &&
    (candidate["reason"] === "same_ro" || candidate["reason"] === "same_vin_and_part")
  );
}

/**
 * The matching cases a 409 `DUPLICATE_CASE` names, or `null` for any other
 * error. A malformed payload yields an empty list rather than a crash: the
 * warning still shows, without links.
 */
export function duplicateCandidatesOf(
  error: unknown,
): readonly DuplicateCandidate[] | null {
  if (!isClientApiError(error) || error.status !== 409 || error.code !== "DUPLICATE_CASE")
    return null;
  const details = error.details as { candidates?: unknown } | null;
  const candidates = details?.candidates;
  if (!Array.isArray(candidates)) return [];
  return candidates.filter(isDuplicateCandidate);
}

/** `fieldErrors` of a 422, or `null` for any other error. */
export function fieldErrorsOf(
  error: unknown,
): Readonly<Record<string, readonly string[]>> | null {
  if (!isClientApiError(error) || error.status !== 422) return null;
  return error.fieldErrors;
}

// ---- request functions ----------------------------------------------------

export function createCase(input: CreateCaseInput): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(orderCommandPaths.create, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateCaseHeader(
  caseId: string,
  input: UpdateCaseHeaderInput,
): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(orderCommandPaths.updateHeader(caseId), {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function cancelCase(
  caseId: string,
  input: CancelCaseInput,
): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(orderCommandPaths.cancel(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function addLine(caseId: string, input: AddLineInput): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(orderCommandPaths.addLine(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateLine(
  caseId: string,
  lineId: string,
  input: UpdateLineInput,
): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(orderCommandPaths.updateLine(caseId, lineId), {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function setLineException(
  caseId: string,
  lineId: string,
  input: SetLineExceptionInput,
): Promise<CaseMutationResultDto> {
  return request<CaseMutationResultDto>(orderCommandPaths.setLineException(caseId, lineId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// ---- hooks ----------------------------------------------------------------

/**
 * Creates an order. On success every case list, count and search query is
 * invalidated (the new case belongs on the work queue); the caller navigates
 * to the created case — or to its receipt — with the id the server returned.
 */
export function useCreateCase(): UseMutationResult<
  CaseMutationResultDto,
  unknown,
  CreateCaseInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCase,
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

export function useUpdateCaseHeader(
  caseId: string,
): UseMutationResult<CaseMutationResultDto, unknown, UpdateCaseHeaderInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCaseHeaderInput) => updateCaseHeader(caseId, input),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

export function useAddLine(
  caseId: string,
): UseMutationResult<CaseMutationResultDto, unknown, AddLineInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddLineInput) => addLine(caseId, input),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

export type UpdateLineVariables = Readonly<{ lineId: string; input: UpdateLineInput }>;

export function useUpdateLine(
  caseId: string,
): UseMutationResult<CaseMutationResultDto, unknown, UpdateLineVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, input }: UpdateLineVariables) => updateLine(caseId, lineId, input),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

export type SetLineExceptionVariables = Readonly<{
  lineId: string;
  input: SetLineExceptionInput;
}>;

/** Flags (`exception` set) or clears (`exception: null`) a line, always with a reason. */
export function useSetLineException<R = CaseMutationResultDto>(
  endpoint: LineExceptionEndpoint<R>,
): UseMutationResult<R, unknown, SetLineExceptionVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, input }: SetLineExceptionVariables) => endpoint(lineId, input),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

/** Cancels the whole order with a reason; the server refuses every later command on it. */
export function useCancelCase<R = CaseMutationResultDto>(
  endpoint: CancelEndpoint<R>,
): UseMutationResult<R, unknown, CancelCaseInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CancelCaseInput) => endpoint(input),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}
